import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useGetState } from 'ahooks';
import toastify from '@/utils/toastify';

import cx from './index.module.scss';

export type HeatInfo = {
  value: number;
  state: Massage.HeatState;
  isShow: boolean;
};

interface Props {}

const state2Text: Record<HeatInfo['state'], string> = {
  LOW: '舒适',
  MEDIUM: '温热',
  HIGH: '过热',
};

export interface HeatTipRefAttrs {
  /**
   * 处理温度变化事件
   */
  handleTemperatureChange: (heatInfo: TemperatureInfo) => void;
}

interface TemperatureInfo {
  temperature: number;
  state: Massage.HeatState;
}

const HeatTip = forwardRef<HeatTipRefAttrs, Props>((_, ref) => {
  const [heatState, setHeatState, getHeatState] = useGetState<HeatInfo>({ state: null, value: null, isShow: false });
  const heatErrorToastClear = useRef<VoidFunc>(null);
  const heatTemperatureDelayTimer = useRef<NodeJS.Timeout>(null);
  const heatStateDelayTimer = useRef<NodeJS.Timeout>(null);
  const [showTipsImg, setShowTipsImg] = useState(false);

  // 处理温度异常警告
  const handleHeatErrorOccur = useCallback(() => {
    if (!heatErrorToastClear.current) {
      const { clear } = toastify.heatInfo.error(
        <div className={cx('heat-info-toast', 'heat-info-toast-error')}>
          <span>皮肤体感发烫难以忍受</span>
          <span className={cx('highlight')}> 44°C以上</span>
        </div>
      );

      heatErrorToastClear.current = clear;
      setShowTipsImg(true);
    }
  }, []);

  // 处理温度异常警告恢复逻辑
  const handleHeatErrorRecover = useCallback(() => {
    heatErrorToastClear.current?.();
    heatErrorToastClear.current = null;
    setShowTipsImg(false);
  }, []);

  // 处理温度变化事件
  const handleTemperatureChange = useCallback(
    (heatInfo: TemperatureInfo) => {
      if (heatInfo?.temperature) {
        clearTimeout(heatTemperatureDelayTimer.current);

        setHeatState({
          value: heatInfo.temperature,
          state: heatInfo.state,
          isShow: true,
        });
      } else {
        heatTemperatureDelayTimer.current = setTimeout(() => {
          setHeatState({
            ...getHeatState(),
            isShow: false,
          });
        }, 3000);
      }

      if (heatInfo?.state === 'HIGH') {
        clearTimeout(heatStateDelayTimer.current);

        handleHeatErrorOccur();
      } else {
        heatStateDelayTimer.current = setTimeout(() => handleHeatErrorRecover(), 3000);
      }
    },
    [getHeatState, handleHeatErrorOccur, handleHeatErrorRecover, setHeatState]
  );

  useImperativeHandle(
    ref,
    () => ({
      handleTemperatureChange,
    }),
    [handleTemperatureChange]
  );

  return (
    <div className={cx('heat-info-wrapper')}>
      {showTipsImg && (
        <div className={cx('tips-img-box')}>
          <div className={cx('tips-img')} />
        </div>
      )}
      <div className={cx('heat-state', heatState?.state?.toLowerCase?.(), { show: heatState?.isShow })}>
        <div className={cx('state')}>{state2Text[heatState?.state]}</div>
        <div className={cx('heat-value')}>{heatState?.value}°C</div>
      </div>
    </div>
  );
});

export default HeatTip;
